import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
	deliveryApi,
	type UpsertCityInput,
	type UpsertNeighborhoodInput,
	type UpsertRegionInput,
} from "@/lib/logistics/delivery-api";

const KEY = ["delivery"] as const;

export const useDeliveryRegions = () =>
	useQuery({
		queryKey: [...KEY, "regions"],
		queryFn: () => deliveryApi.regions(),
		staleTime: 30_000,
	});

export const useDeliveryCities = (region?: string) =>
	useQuery({
		queryKey: [...KEY, "cities", region ?? "all"],
		queryFn: () => deliveryApi.cities(region),
		staleTime: 30_000,
	});

export const useUpdateRegion = () => {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: ({ id, body }: { id: string; body: UpsertRegionInput }) =>
			deliveryApi.updateRegion(id, body),
		onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
	});
};

export const useCreateCity = () => {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (body: UpsertCityInput) => deliveryApi.createCity(body),
		onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
	});
};

export const useUpdateCity = () => {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: ({ id, body }: { id: string; body: UpsertCityInput }) =>
			deliveryApi.updateCity(id, body),
		onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
	});
};

export const useDeleteCity = () => {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (id: string) => deliveryApi.removeCity(id),
		onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
	});
};

export const useDeliveryNeighborhoods = (cityId?: string) =>
	useQuery({
		queryKey: [...KEY, "neighborhoods", cityId ?? "all"],
		queryFn: () => deliveryApi.neighborhoods(cityId),
		// Sans ville choisie, la liste complète n'apprend rien : on attend.
		enabled: Boolean(cityId),
		staleTime: 30_000,
	});

export const useCreateNeighborhood = () => {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (body: UpsertNeighborhoodInput) =>
			deliveryApi.createNeighborhood(body),
		onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
	});
};

export const useUpdateNeighborhood = () => {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: ({ id, body }: { id: string; body: UpsertNeighborhoodInput }) =>
			deliveryApi.updateNeighborhood(id, body),
		onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
	});
};

export const useDeleteNeighborhood = () => {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (id: string) => deliveryApi.removeNeighborhood(id),
		onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
	});
};
